import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import { toast } from "sonner";
import ClientInfoRepository from "../repositories/ClientInfoRepository";
import {UserRole} from "../models/User";
import ConfirmationModal from "../components/common/ConfirmationModal";
import { ClientsTable } from "../components/Clients/ClientsTable";
import {QUERY_KEYS} from "../constants/QueryKeys";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

interface ClientsPageProps {
  userRole: UserRole;
  isAssignedUser: boolean;
}

const ClientsPage: React.FC<ClientsPageProps> = ({
  userRole,
  isAssignedUser
}: ClientsPageProps) => {
  const queryClient = useQueryClient();
  const clientInfoRepository = new ClientInfoRepository();
  const { organizationId } = useParams();
  const [deleteClientId, setDeleteClientId] = useState<string | null>(null);
  const [deleteClientName, setDeleteClientName] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [uninstallClientId, setUninstallClientId] = useState<string | null>(null);
  const [uninstallClientName, setUninstallClientName] = useState<string | null>(null);
  const [isUninstallModalOpen, setIsUninstallModalOpen] = useState(false);
  const [forceRemoveClientId, setForceRemoveClientId] = useState<string | null>(null);
  const [forceRemoveClientName, setForceRemoveClientName] = useState<string | null>(null);
  const [isForceRemoveModalOpen, setIsForceRemoveModalOpen] = useState(false);
  const [resetOrganizationId, setResetOrganizationId] = useState<string | null>(null);
  const [resetClientId, setResetClientId] = useState<string | null>(null);
  const [resetVersion, setResetVersion] = useState<string | null>(null);
  const [resetAppType, setResetAppType] = useState<string | null>(null);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const handleSoftDeleteClient = (clientId: string, clientName: string) => {
    setDeleteClientId(clientId);
    setDeleteClientName(clientName);
    setIsDeleteModalOpen(true);
  };

  const handleCloseSoftDeleteClientModal = () => {
    setDeleteClientId(null);
    setDeleteClientName(null);
    setIsDeleteModalOpen(false);
  };

  const handleUninstallClient = (clientId: string, clientName: string) => {
    setUninstallClientId(clientId);
    setUninstallClientName(clientName);
    setIsUninstallModalOpen(true);
  };

  const handleCloseUninstallClientModal = () => {
    setUninstallClientId(null);
    setUninstallClientName(null);
    setIsUninstallModalOpen(false);
  };

  const handleForceRemoveClient = (clientId: string, clientName: string) => {
    setForceRemoveClientId(clientId);
    setForceRemoveClientName(clientName);
    setIsForceRemoveModalOpen(true);
  };

  const handleCloseForceRemoveModal = () => {
    setForceRemoveClientId(null);
    setForceRemoveClientName(null);
    setIsForceRemoveModalOpen(false);
  };

  const handleConfirmForceRemove = async () => {
    if (forceRemoveClientId) {
      const name = forceRemoveClientName;
      try {
        await clientInfoRepository.forceRemoveClient(forceRemoveClientId);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLIENT_INFOS, organizationId] });
        toast.success(name ? `Client "${name}" force removed` : "Client force removed");
      } catch (error: any) {
        toast.error(error?.message ?? "Failed to force remove client");
      }
    }

    handleCloseForceRemoveModal();
  };

  const handleResetBlacklistedVersion = (organizationId: string, clientId: string, version: string, applicationType: string) => {
    setResetOrganizationId(organizationId);
    setResetClientId(clientId);
    setResetVersion(version);
    setResetAppType(applicationType);
    setIsResetModalOpen(true);
  };

  const handleCloseResetModal = () => {
    setResetOrganizationId(null);
    setResetClientId(null);
    setResetVersion(null);
    setResetAppType(null);
    setIsResetModalOpen(false);
  };

  const resetFailedInstallationMutation = useMutation({
    mutationFn: ({ organizationId, clientId, version, applicationType }: { organizationId: string; clientId: string; version: string; applicationType: string }) =>
      clientInfoRepository.resetFailedInstallation(organizationId, clientId, version, applicationType),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLIENT_INFOS, organizationId] }),
  });

  const updateClientConfigMutation = useMutation({
    mutationFn: ({ clientId, newConfigId, reason }: { clientId: string; newConfigId: string | null; reason?: string }) =>
      clientInfoRepository.setActiveConfig(clientId, newConfigId, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLIENT_INFOS, organizationId] }),
  });

  const updateClientCacheSettingsMutation = useMutation({
    mutationFn: ({ clientId, useCache, useDeltas, rebuildCache, reason }: { clientId: string; useCache: boolean; useDeltas: boolean; rebuildCache: boolean; reason?: string }) =>
      clientInfoRepository.patchClientCacheSettings(clientId, useCache, useDeltas, rebuildCache, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLIENT_INFOS, organizationId] }),
  });

  /**
   * Awaitable wrappers so ActionConfirmationModal can show success/error
   * toasts based on the actual mutation outcome (rather than firing the
   * success toast immediately when the user clicks confirm).
   */
  const handleConfigChange = async (
    clientId: string,
    newConfigId: string | null,
    reason?: string,
  ) => {
    await updateClientConfigMutation.mutateAsync({ clientId, newConfigId, reason });
  };

  const handleCacheSettingsChange = async (
    clientId: string,
    useCache: boolean,
    useDeltas: boolean,
    rebuildCache: boolean,
    reason?: string,
  ) => {
    await updateClientCacheSettingsMutation.mutateAsync({
      clientId,
      useCache,
      useDeltas,
      rebuildCache,
      reason,
    });
  };

  const handleConfirmReset = async () => {
    if (resetOrganizationId && resetClientId && resetVersion && resetAppType) {
      const version = resetVersion;
      try {
        await resetFailedInstallationMutation.mutateAsync({
          organizationId: resetOrganizationId,
          clientId: resetClientId,
          version: resetVersion,
          applicationType: resetAppType
        });
        toast.success(`Version ${version} reset`);
      } catch (error: any) {
        toast.error(error?.message ?? "Failed to reset version");
      }
    }

    handleCloseResetModal();
  };

  const handleConfirmDelete = async () => {
    if (deleteClientId) {
      const name = deleteClientName;
      try {
        await clientInfoRepository.deleteClient(deleteClientId);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLIENT_INFOS, organizationId] });
        toast.success(name ? `Client "${name}" deleted` : "Client deleted");
      } catch (error: any) {
        toast.error(error?.message ?? "Failed to delete client");
      }
    }

    handleCloseSoftDeleteClientModal();
  };

  const handleConfirmUninstall = async () => {
    if (uninstallClientId) {
      const name = uninstallClientName;
      try {
        await clientInfoRepository.uninstallClient(uninstallClientId);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLIENT_INFOS, organizationId] });
        toast.success(
          name
            ? `Client "${name}" set to Pending Uninstall. The updater removes it at its next check.`
            : "Client set to Pending Uninstall. The updater removes it at its next check."
        );
      } catch (error: any) {
        toast.error(error?.message ?? "Failed to uninstall client");
      }
    }

    handleCloseUninstallClientModal();
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex my-2 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between shrink-0">
        <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">Clients<DocsHelpLink to="configuration/clients" /></h2>
      </div>
      <ClientsTable
        organizationId={organizationId ?? ""}
        onConfigChange={handleConfigChange}
        onCacheSettingsChange={handleCacheSettingsChange}
        onSoftDeleteClient={async (clientId, clientName) => {handleSoftDeleteClient(clientId, clientName);}}
        onForceRemoveClient={async (clientId, clientName) => {handleForceRemoveClient(clientId, clientName);}}
        onUninstallClient={async (clientId, clientName) => {handleUninstallClient(clientId, clientName);}}
        onCancelUninstallClient={async (clientId, clientName) => {
          try {
            await clientInfoRepository.cancelUninstall(clientId);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLIENT_INFOS, organizationId] });
            toast.success(`Uninstall of "${clientName}" cancelled`);
          } catch (error: any) {
            toast.error(error?.message ?? "Failed to cancel the uninstall");
          }
        }}
        onResetBlacklistedVersion={async (organizationId, clientId, version, applicationType) => { handleResetBlacklistedVersion(organizationId, clientId, version, applicationType);}}
        userRole={userRole}
        isAssignedUser={isAssignedUser}
      />
      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={handleCloseSoftDeleteClientModal}
        onConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this client?"
        statusColor="danger"
        buttonText="Delete"
      >
        <div className="flex flex-wrap">
          <Badge variant="outline">
            {deleteClientName}
          </Badge>
        </div>
      </ConfirmationModal>
      <ConfirmationModal
        open={isUninstallModalOpen}
        onClose={handleCloseUninstallClientModal}
        onConfirm={handleConfirmUninstall}
        message="Are you sure you want to uninstall this client?"
        statusColor="warning"
        buttonText="Uninstall"
      >
        <div className="flex flex-wrap">
          <Badge variant="outline">
            {uninstallClientName}
          </Badge>
        </div>
      </ConfirmationModal>
      <ConfirmationModal
        open={isForceRemoveModalOpen}
        onClose={handleCloseForceRemoveModal}
        onConfirm={handleConfirmForceRemove}
        message="Are you sure you want to force remove this client? This bypasses the standard uninstall flow."
        statusColor="danger"
        buttonText="Force Remove"
      >
        <div className="flex flex-wrap">
          <Badge variant="outline">
            {forceRemoveClientName}
          </Badge>
        </div>
      </ConfirmationModal>
      <ConfirmationModal
        open={isResetModalOpen}
        onClose={handleCloseResetModal}
        onConfirm={handleConfirmReset}
        message="Are you sure you want to remove this version from the blacklist?"
        statusColor="warning"
        buttonText="Remove"
      >
        <div className="flex flex-wrap">
          <Badge variant="outline">
            {resetVersion}
          </Badge>
        </div>
      </ConfirmationModal>
    </div>
  );
};

export default ClientsPage;
